import { cn } from "@/utils/cn";
import { Check } from "lucide-react";

export type SourceKey = "trial_registries" | "regulatory" | "patents" | "sec_filings" | "geo_datasets" | "proteins";
export type RowSource = "papers" | SourceKey;

/** The sources a dataset can search besides papers (which include arXiv), in the builder planner's names. */
export const SOURCES: { key: SourceKey; label: string }[] = [
  { key: "trial_registries", label: "Clinical trials" },
  { key: "regulatory", label: "FDA, EMA, PMDA" },
  { key: "patents", label: "Patents" },
  { key: "sec_filings", label: "SEC filings" },
  { key: "geo_datasets", label: "GEO datasets" },
  { key: "proteins", label: "Proteins" },
];
const KEYS = new Set<string>(SOURCES.map((s) => s.key));

/** rowSource is the planner's: whose records the rows are. It follows the sources the user leaves on. */
export type SourceChoice = { papers: boolean; sources: SourceKey[]; rowSource: RowSource };

/** The planner's sources and row source, as the review starts from them. */
export const plannedSources = (
  state: { sources?: string[] | null; row_source?: string | null; papers?: boolean | null } | null | undefined,
): SourceChoice => {
  const sources = (state?.sources ?? []).filter((s): s is SourceKey => KEYS.has(s));
  const papers = state?.papers !== false || !sources.length;
  const row = state?.row_source;
  return { papers, sources, rowSource: rowFor(papers, sources, row && (row === "papers" || sources.includes(row as SourceKey)) ? (row as RowSource) : "papers") };
};

/** The row source, moved to a source still searched when its own is turned off. */
const rowFor = (papers: boolean, sources: SourceKey[], row: RowSource): RowSource =>
  row === "papers" ? (papers ? "papers" : sources[0]) : sources.includes(row) ? row : papers ? "papers" : sources[0];

const chip =
  "font-ui inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-400 disabled:cursor-not-allowed";

/**
 * Where the dataset searches, before anything is read: the sources the planner picked, papers included, which the
 * user can add to or remove. At least one stays on.
 */
export const SourceReview = ({ value, onChange }: { value: SourceChoice; onChange: (next: SourceChoice) => void }) => {
  const count = (value.papers ? 1 : 0) + value.sources.length;
  const set = (papers: boolean, sources: SourceKey[]) => onChange({ papers, sources, rowSource: rowFor(papers, sources, value.rowSource) });
  const toggle = (key: SourceKey) =>
    set(value.papers, value.sources.includes(key) ? value.sources.filter((s) => s !== key) : SOURCES.map((s) => s.key).filter((k) => k === key || value.sources.includes(k)));
  const chips: { key: string; label: string; on: boolean; title?: string; flip: () => void }[] = [
    { key: "papers", label: "Papers", on: value.papers, title: "PubMed Central, PubMed, bioRxiv, medRxiv and arXiv", flip: () => set(!value.papers, value.sources) },
    ...SOURCES.map((s) => ({ key: s.key, label: s.label, on: value.sources.includes(s.key), flip: () => toggle(s.key) })),
  ];

  return (
    <div className="border-b border-zinc-300 py-3" aria-label="Review sources" role="group">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-ui mr-1 w-16 text-3xs uppercase tracking-wider text-zinc-400">Search</span>
        {chips.map((c) => {
          const last = c.on && count === 1;
          return (
            <button
              key={c.key}
              type="button"
              aria-pressed={c.on}
              disabled={last}
              title={last ? "At least one source is searched" : c.title}
              onClick={c.flip}
              className={cn(chip, c.on ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-500 hover:border-zinc-400 hover:text-zinc-800")}
            >
              {c.on && <Check className="h-3 w-3" aria-hidden />}
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
